import React from 'react';
import { useTranslation } from 'react-i18next';
import { Recycle, Zap, Clock, Leaf } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const iconMap = {
  Recycle: Recycle,
  Zap: Zap,
  Clock: Clock,
  Leaf: Leaf
};

export const Sustainability = () => {
  const { t } = useTranslation();

  const initiatives = [
    { icon: 'Recycle', key: 'recycled' },
    { icon: 'Zap', key: 'energy' },
    { icon: 'Clock', key: 'lifecycle' },
    { icon: 'Leaf', key: 'recyclable' }
  ];

  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-emerald-900 text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">{t('sustainability.subtitle')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6">{t('sustainability.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-300 max-w-3xl leading-relaxed">
            {t('sustainability.description')}
          </p>
        </div>
      </section>

      {/* Sustainability Initiatives */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('sustainability.initiativesSubtitle')}</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">{t('sustainability.initiatives')}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {initiatives.map((initiative, index) => {
              const IconComponent = iconMap[initiative.icon];
              return (
                <Card key={index} className="text-center hover:shadow-2xl transition-all duration-300 border-0 shadow-lg card-hover group">
                  <CardContent className="pt-6 sm:pt-8 lg:pt-10 pb-4 sm:pb-6 lg:pb-8 px-3 sm:px-4 lg:px-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 mb-3 sm:mb-4 lg:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                      <IconComponent className="w-5 h-5 sm:w-7 sm:h-7 lg:w-9 lg:h-9" />
                    </div>
                    <h3 className="font-semibold text-sm sm:text-base lg:text-xl mb-2 sm:mb-4 text-neutral-900">{t(`sustainability.${initiative.key}`)}</h3>
                    <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed hidden sm:block">{t(`sustainability.${initiative.key}Desc`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-10 sm:py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-6 sm:mb-8">Environmental Impact</h2>
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-emerald-50 border-l-4 border-emerald-600 p-4 sm:p-6">
                <h3 className="font-semibold text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 text-emerald-900">Circular Economy</h3>
                <p className="text-neutral-700 text-xs sm:text-sm lg:text-base">
                  Our bins are designed with the circular economy in mind. We use recycled materials in production and ensure that every product can be fully recycled at the end of its life cycle, reducing waste and environmental impact.
                </p>
              </div>
              <div className="bg-emerald-50 border-l-4 border-emerald-600 p-4 sm:p-6">
                <h3 className="font-semibold text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 text-emerald-900">Carbon Footprint Reduction</h3>
                <p className="text-neutral-700 text-xs sm:text-sm lg:text-base">
                  By manufacturing locally and optimizing our logistics, we minimize transportation emissions. Our energy-efficient production processes further reduce our carbon footprint.
                </p>
              </div>
              <div className="bg-emerald-50 border-l-4 border-emerald-600 p-4 sm:p-6">
                <h3 className="font-semibold text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 text-emerald-900">Quality for Longevity</h3>
                <p className="text-neutral-700 text-xs sm:text-sm lg:text-base">
                  We believe that the most sustainable product is one that lasts. Our bins are engineered for durability, with an expected lifespan of over 10 years, reducing the need for frequent replacements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4 sm:gap-8 lg:gap-12 text-center">
            <div className="group">
              <div className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 sm:mb-4 group-hover:scale-110 transition-transform">80%</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('sustainability.stats.recycledContent')}</div>
            </div>
            <div className="group">
              <div className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 sm:mb-4 group-hover:scale-110 transition-transform">100%</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('sustainability.stats.recyclableProducts')}</div>
            </div>
            <div className="group">
              <div className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 sm:mb-4 group-hover:scale-110 transition-transform">10+</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('sustainability.stats.lifespan')}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sustainability;